"use client";

import * as _ from "lodash-es";
import Carousel from "./Carousel";
import Container, {ContainerProps} from "./Container";
import StrapiImage, {StrapiImageData} from "./StrapiImage";
import TestimonialType from "../types/Testimonial";
import CarouselControls from "./CarouselControls";
import {useState} from "react";
import OverlineWithLine from "./decorations/OverlineWithLine";
import {FCWithHTMLAttributes} from "../types/reactTypes";

const TESTIMONIALS_PER_PAGE = 2;

interface TestimonialProps {
	icon: StrapiImageData;
	testimonial: TestimonialType;
}

const Testimonial: React.FC<TestimonialProps> = ({icon, testimonial}) => (
	<div className="flex w-full flex-col items-center gap-4 lg:items-start">
		<StrapiImage
			image={icon}
			width={icon.attributes.width}
			height={icon.attributes.height}
		/>
		<p className="max-w-[calc(100vw-2rem)] text-body-2">“{testimonial.body}”</p>
		<h3>{testimonial.name}</h3>
		<p className="font-overline text-gray-600">{testimonial.role}</p>
	</div>
);

interface TestimonialsProps extends ContainerProps {
	data: {
		overline: string;
		heading: string;
		ratingIcon: {data: StrapiImageData};
		testimonials: {
			data: {
				attributes: TestimonialType;
			}[];
		};
	};
}

const Testimonials: FCWithHTMLAttributes<TestimonialsProps> = ({
	data,
	...props
}) => {
	const {overline, heading, ratingIcon} = data;
	const testimonials: TestimonialType[] = data.testimonials.data.map(
		(t: any) => t.attributes
	);
	const [page, setPage] = useState<number | null>(null);
	const testimonialPages = _.chunk(testimonials, TESTIMONIALS_PER_PAGE);
	return (
		<Container
			className="relative flex flex-col items-center text-center md:py-20 lg:items-start lg:text-left"
			{...props}
		>
			<OverlineWithLine className="mb-4 justify-center" overline={overline} />
			<h2 className="mb-10">{heading}</h2>
			<div className="max-w-full overflow-x-hidden">
				<Carousel
					className="mb-10"
					allowScroll={false}
					page={page}
					pageClassName="w-full shrink-0"
				>
					{testimonialPages.map(
						(testimonialPage: TestimonialType[], pageIndex) => (
							<div
								key={pageIndex}
								className="flex w-full shrink-0 flex-col gap-10 lg:flex-row"
							>
								{testimonialPage.map((testimonial, testimonialIndex) => (
									<Testimonial
										key={testimonial.name + testimonialIndex}
										icon={ratingIcon.data}
										testimonial={testimonial}
									/>
								))}
							</div>
						)
					)}
				</Carousel>
				<CarouselControls
					numPages={testimonialPages.length}
					page={page ?? 0}
					setPage={setPage}
				/>
			</div>
		</Container>
	);
};

export default Testimonials;
